import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  combineLatest,
  debounceTime,
  finalize,
  of,
  Subject,
  Subscription,
  take,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone';

import { ICapacity, Resource, ResourceAssignedToFloorplan } from '@shared/resource/models';
import { Schedule } from '@shared/schedule/models';
import { MergedCustomFields, Party } from '@shared/party/models';
import { BlockedTimeResult } from '@shared/blocked-time/models';
import { ServiceWithAvailability } from '@shared/service/models';
import { getDurationForPartySize } from '@shared/service/functions';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Restaurant } from '@shared/restaurant/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { mergeCustomFields, canBypassNewPartyValidation, partyCriticalFieldsUnchanged, PartyCriticalFields, partyIdsEqual, isPartySeated } from '@shared/party';
import { describeOutOfBusinessHours } from '@shared/schedule/functions';
import { ResourceSuggestionOption } from '@shared/resource/enums';
import {
  CommonRadioButtonItem,
  ConfigurePartyService,
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  FloorplanResources,
  IParty,
  ProviderScheduleService,
  TelService,
  calculateShortenedDurationState,
  getServiceOptionNameWithWarning,
  ShortenedDurationState,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { CustomerInfo } from '@tablesready/frontend-party-customer-info';
import { PartiesService } from '@tablesready/frontend-api-services';
import { WaitTimeService } from '@tablesready/frontend-party-observer';
import {
  ActivePartiesService,
  DateService,
  MarkPartyService,
  ResourcesService,
  ServicesService,
  AutoDurationRecalculator,
} from '../../../services';
import { CustomersService } from '../../../customers/services';
import { AddEditPartyService } from '../../../services/add-edit-party.service';

@Component({
  selector: 'tablesready-add-edit-party-floorplan',
  templateUrl: './add-edit-party-floorplan.component.html',
  styleUrls: ['./add-edit-party-floorplan.component.scss'],
  standalone: false,
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
})
export class AddEditPartyFloorplanComponent implements OnInit, OnDestroy {
  @Input() partyToEdit: IParty;
  @Input() typeOfParty: PartyTypes;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() selectedServices;
  @Input() showSelectResourceMessage: boolean;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() selectedEndTime: number;
  @Input() selectedDate: NgbDateStruct;
  @Input() floorplanResources: FloorplanResources[];
  @Input() availableOnBusinessPlan = false;
  @Input() resourceOverrideCallback?: (party: IParty) => void;
  @Input() afterInit?: (instance: AddEditPartyFloorplanComponent) => void;

  title: string;
  angularticsActionType: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  now: number;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: ServiceWithAvailability[] = [];
  // Shortened duration tracking
  shortenedDurationState: ShortenedDurationState | null = null;
  shortenedDurationConfirmed = false;

  msPerMin = 60000;
  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  resourceSizeAlert: string | null = null;
  generalSchedule: Schedule[];
  overrideNoResources = false;
  overrideBlockedTime = false;
  blockedTime?: BlockedTimeResult;
  exceptions;
  reservationMinutesPast = 420;
  sizeOther: number;
  icon = AppIcon;
  party;
  time;
  date;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  removeServices = false;
  removeResources = false;
  showSizeValidationMessage;
  schedule;
  overrideIsWorkTime = false;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  noteOptions: string[] | string;
  serviceSettingsLabel: string;
  resourceSettingsLabel: string;
  displayOptionCustomerLabel: string;
  displayOptionsReservationsLabel: string;
  displayOptionsReservationsLabelPlural: string;
  sizesRadioItems: CommonRadioButtonItem[] = [];
  servicesEnabled = false;
  partySources = PartySource;
  resourceSuggestionOption = ResourceSuggestionOption;
  customer: CustomerInfo = null;

  private overlappingParty: Party[];
  private allowMultipleParties: boolean;
  private resourcesEnabled = false;
  private onlineReservationEnabled: boolean;
  private partySizeForTimeSlots: boolean;
  private bookingsPerTimeSlotEnabled = false;
  private bookingsPerTimeSlotByGuests = false;
  private bookingsPerTimeSlotQuantity = 1;
  private isEditMode = false;
  private originalCritical: PartyCriticalFields | null = null;
  private hasDisabledServicesOnInit = false;
  private readonly durationRecalc = new AutoDurationRecalculator({
    getServices: () => this.servicesInDropdown,
    getSelectedServiceIds: () => this.party.services || [],
    getPartySize: () => this.party.size,
    getDuration: () => this.party.duration,
    applyDuration: (duration) => {
      this.party.duration = duration;
      this.setPartyDurationInMinutes();
    },
  });

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;
  @ViewChild('addEditForm') addEditForm: NgForm;

  private readonly getAvailableResourcesServices$ = new Subject<{ date: string, party: IParty }>();
  private readonly subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly configureParty: ConfigurePartyService,
    private readonly clockService: ClockService,
    private readonly telService: TelService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly customerService: CustomersService,
    private readonly addEditPartyService: AddEditPartyService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly activePartiesService: ActivePartiesService,
  ) { }

  isOutOfBusinessHours(formGroup: NgForm): boolean {
    // Editing an existing party must never re-run new-party business-hours validation —
    // it was already valid when saved; only a brand-new party needs this check.
    if (this.isEditMode && isPartySeated(this.partyToEdit)) {
      return false;
    }

    return (!this.isWorkTime || this.isPartyInPastTime) && this.isTimeFieldValid(formGroup);
  }

  getOutOfBusinessHoursMessage(): string {
    return describeOutOfBusinessHours(
      this.currentTime.valueOf(),
      this.party?.duration || 0,
      this.workHours || [],
      this.timezone,
    );
  }

  canBypassWorkTime(): boolean {
    return canBypassNewPartyValidation(
      this.isEditMode, this.partyToEdit, this.originalCritical, () => this.snapshotCritical(),
    );
  }

  private captureOriginalCritical(): void {
    this.originalCritical = this.snapshotCritical();
  }

  // quotedTime uses currentTime (the live, edited reservation time). this.party.quoted_time is only
  // written at save time (convertQuotedTime), so it is stale during editing.
  private snapshotCritical(): PartyCriticalFields {
    return {
      size: this.party.size,
      duration: this.party.duration,
      quotedTime: this.currentTime.valueOf(),
      services: [...(this.party.services ?? [])],
      resources: [...(this.party.resources ?? [])],
      seatedTime: this.partyToEdit.seated_time ?? null,
      checkinTime: this.partyToEdit.checkin_time ?? null,
    };
  }

  private criticalFieldsUnchanged(): boolean {
    return partyCriticalFieldsUnchanged(this.originalCritical, () => this.snapshotCritical());
  }

  private requiresBusinessHoursOverride(): boolean {
    return (!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime;
  }

  isTimeFieldValid({ form: addEditForm }: NgForm): boolean {
    const time = addEditForm.get('time')?.value;

    return time !== '' && time !== undefined;
  }

  /**
   * The time-block warning and its "Override?" checkbox. A block reserves the slot against *new*
   * reservations, so it says nothing about a party that is already sitting at the table — the
   * warning is not raised for a served party. The Confirm button mirrors this predicate, so hiding
   * the warning also drops its gate; leaving the gate would disable Confirm with nothing on screen
   * to explain it
   */
  showBlockedTimeWarning(): boolean {
    return this.isReservation
      && Boolean(this.blockedTime?.blocked)
      && !this.overrideBlockedTime
      && !isPartySeated(this.partyToEdit);
  }

  noAvailableServices(formGroup: NgForm): boolean {
    // A seated party is not re-validated against current availability — an empty dropdown caused by
    // the schedule says nothing about a party already at the table, and this warning offers no
    // override, so leaving it on would block the edit outright. A selection that no single service
    // can cover is different: the user just made it, and it cannot be saved.
    return this.servicesInDropdown.length === 0 && this.servicesEnabled &&
      this.isTimeFieldValid(formGroup) && !this.isOutOfBusinessHours(formGroup) &&
      (!isPartySeated(this.partyToEdit) || this.selectedResourcesShareNoService());
  }

  /**
   * Whether the selected resources have no service in common — one table assigned to Service 1 and
   * another to Service 2, say. Nothing can serve that pair, so the warning is raised (and Confirm
   * gated) even for a served party, whose availability is otherwise left alone. A single resource
   * cannot conflict with itself, and an id the store cannot resolve is not guessed at.
   */
  private selectedResourcesShareNoService(): boolean {
    const selectedIds = this.party?.resources ?? [];
    if (selectedIds.length < 2) {
      return false;
    }

    const resources = this.resourcesService.getEnabledByIds(selectedIds);
    if (resources.length !== selectedIds.length) {
      return false;
    }

    return resources
      .map(resource => new Set(resource.services ?? []))
      .reduce((shared, services) => new Set([...shared].filter(id => services.has(id))))
      .size === 0;
  }

  get isAvailableTime() {
    if (!this.isWorkTime) {
      return false;
    }

    if (!this.overrideNoResources && this.isSlotFullByGuests()) {
      return true;
    }

    return this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  get isWalkin() {
    return this.typeOfParty === PartyTypes.walkIn;
  }

  get isReservation() {
    return this.typeOfParty === PartyTypes.reservation;
  }

  get isPartyInPastTime() {
    return this.currentTime.valueOf() < Date.now();
  }

  ngOnInit(): void {
    const getAvailableResourcesSub = this.getAvailableResourcesServices$
      .pipe(debounceTime(500))
      .subscribe((data) => {
        this.getAvailableResourcesServicesInternal(data.date, data.party);
      });
    this.subscription.add(getAvailableResourcesSub);

    this.sizesRadioItems = [1, 2, 3, 4, 5, 6, 7].map((size) => ({
      name: String(size),
      value: size,
      onClick: () => this.sizeChanged(),
    }));

    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));

    this.initModalProp();
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();

      if (!this.hasGuestPerSlotLimit && !this.isAvailableTime && this.party.resources.length > 0) {
        this.overrideNoResources = true;
      }

      this.checkWorkTime();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }
    this.setTitle();

    if (this.afterInit) {
      this.afterInit(this);
    }
  }

  initModalProp() {
    this.noteOptions = this.user?.noteOptions || [];
    this.serviceSettingsLabel = this.user?.serviceSettings?.label || 'service';
    this.resourceSettingsLabel = this.user.resourceSettings?.label || 'resource';
    this.displayOptionCustomerLabel = this.user.displayOptions?.labels?.customer || 'customer';
    this.displayOptionsReservationsLabel = this.user.displayOptions?.labels?.reservation || 'reservation';
    this.displayOptionsReservationsLabelPlural = this.user.displayOptions?.labels?.reservation_plural || 'reservations';
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.onlineReservationEnabled = this.user?.bookingOptions?.onlineReservationsEnabled;
    this.partySizeForTimeSlots = this.user?.bookingOptions?.partySizeForTimeSlots;
    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);

    const perTimeSlot = this.user?.bookingOptions?.bookingsAllowedPerTimeSlot;
    this.bookingsPerTimeSlotEnabled = perTimeSlot?.enabled ?? false;
    this.bookingsPerTimeSlotByGuests = perTimeSlot?.countByTotalGuests ?? false;
    this.bookingsPerTimeSlotQuantity = perTimeSlot?.quantity ?? 1;

    try {
      this.schedule = this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.timezone = this.user.timezone;
    this.currentTime = moment.tz(this.timezone);
    this.exceptions = this.generalSchedule[0]?.exceptions || [];
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;

    const subscription = combineLatest([
      this.resourcesService.isEnabled(),
      this.servicesService.isEnabled(),
    ])
      .pipe(tap(([resourcesEnabled, servicesEnabled]) => {
        this.resourcesEnabled = resourcesEnabled;
        this.servicesEnabled = servicesEnabled;
        this.overrideNoResources = !this.hasGuestPerSlotLimit &&
          !(this.servicesEnabled || this.user.bookingOptions?.onlineReservationsEnabled);
      }))
      .subscribe();
    this.subscription.add(subscription);
  }

  setTitle(): void {
    let text = this.user?.displayOptions?.labels?.customer || 'customer';

    if (this.typeOfParty === PartyTypes.reservation) {
      text = this.user.displayOptions?.labels?.reservation || 'reservation';
    }

    this.title += text;
  }

  initEditMode(): void {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited reservation';
    this.party = cloneDeep(this.partyToEdit);
    this.selectedTimeMode = false;
    this.isEditMode = true;
    const quotedTime = moment.tz(this.party.quoted_time, this.timezone);
    // Only enabled services/resources are shown as selected and offered in the dropdown — a
    // disabled assignment is never user-visible or user-selectable. The true original ids
    // (this.partyToEdit.services/.resources) remain available for the autoselect guard below and
    // for saveParty(), which restores them on save if the user never touched these fields, so a
    // disabled assignment is preserved rather than silently dropped by an unrelated edit.
    const enabledServices = this.servicesService.getEnabledByIds(this.partyToEdit.services);
    this.hasDisabledServicesOnInit = enabledServices.length !== (this.partyToEdit.services || []).length;
    this.party.services = enabledServices.map(service => service._id);
    if (this.hasDisabledServicesOnInit) {
      this.party.duration = enabledServices.reduce((sum, svc) => sum + getDurationForPartySize(svc, this.party.size), 0);
    }
    this.durationRecalc.setBaseline(
      this.dateService.checkServicesDuration(enabledServices, this.party.services, this.party.size),
      this.party.services,
    );
    this.party.resources = this.resourcesService.getEnabledByIds(this.partyToEdit.resources).map(resource => resource._id);
    this.setPartyDurationInMinutes();
    this.party.phone = this.telService.format(this.party.phone, this.user.country, 'custom');
    this.party.quoted_time_minutes = this.typeOfParty === PartyTypes.walkIn ? Math.round((this.party.quoted_time - this.now) / this.msPerMin) : 0;
    this.party.date = this.dateTimeService.momentToNgbDate(quotedTime);
    this.party.time = quotedTime.format('h:mm A');
    this.originalQuotedTime = this.party.quoted_time;
    this.currentTime = moment(this.party.quoted_time).tz(this.timezone);
    this.setPartySize(this.party.size);
    this.isWorkTime = true;
    this.captureOriginalCritical();

    if (!this.selectedResource) {
      return;
    }

    const selectedResourceCapacity = this.calculateResourcesCapacity([this.selectedResource]);
    this.showSizeAlert(selectedResourceCapacity);
  }

  setPartySize(size) {
    if (size < 8) {
      this.party.size = size;

      return true;
    }
    this.sizeOther = this.party.size;
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    this.party = {
      quoted_time: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: Math.max(this.selectedResource.capacity.min, Math.min(this.user.default_size, this.selectedResource.capacity.max)),
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      customFields: {},
    };
    this.currentTime = selectedTime;
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  initAddMode() {
    this.title = 'Add ';
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = false;
    this.party = {
      quoted_time_minutes: this.typeOfParty === PartyTypes.reservation ? 0 : this.waitTimeService.getTime(),
      size: this.user.default_size,
      type: this.getPartyType(this.typeOfParty),
      date: this.selectedDate,
      customFields: {},
    };
    this.party.quoted_time = this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.checkWorkTime();
    this.setPartySize(this.party.size);
  }

  getPartyType(typeOfParty: PartyTypes): PartyTypes {
    return typeOfParty === PartyTypes.reservation ? PartyTypes.reservation : PartyTypes.walkIn;
  }

  checkWorkTime(): void {
    if (!this.servicesEnabled && this.resourcesEnabled) {
      this.getAvailableResourcesServices$.next({
        date: this.currentTime.format('x'),
        party: this.party,
      });
    }

    if (this.typeOfParty === PartyTypes.reservation && !this.party.time) {
      return;
    }

    this.isSubmitting = true;
    this.time = this.party.time || this.currentTime.format('h:mm A');
    this.date = this.currentTime.format('D-M-YYYY');

    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService
      .getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    if (!this.criticalFieldsUnchanged() && this.party.duration <= 0) {
      this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services, this.party.size);
    }

    this.setPartyDurationInMinutes();

    this.isWorkTime = this.providerScheduleService
      .comparisonTime(this.currentTime.valueOf(), this.workHours, this.party.duration);
    if (this.servicesEnabled || this.resourcesEnabled) {
      this.getAvailableResourcesServices$.next({
        date: this.currentTime.format('x'),
        party: this.party,
      });
    } else {
      this.isSubmitting = false;
    }
  }

  getAvailableResourcesServicesInternal(date, party: IParty) {
    const { services, resources: resourceIds, size, duration } = party;
    if (!date && !services && !resourceIds) {
      return false;
    }

    const type = party.type === PartyTypes.reservation ? party.type : PartyTypes.walkIn;
    const dateMilliseconds = this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone).valueOf();
    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      dateMilliseconds,
      this.timezone,
    );

    this.partiesService.findResourcesServicesForNewParty(
      date,
      type,
      // Not going to send services, to get same result from back-end every time
      // this.overrideNoResources ? services : [],
      services,
      // this.overrideNoResources ? [] : resourceIds,
      resourceIds,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
      .pipe(
        take(1),
        tap((data) => {
          let resourcesCapacity: ICapacity = null;
          this.showOverlappingAlert = false;
          this.overlappingParty = data?.overlappingParty;
          this.blockedTime = data?.blockedTime;

          if (data?.availableResources) {
            this.resourcesInDropdown = this.addEditPartyService.placeResourcesFitBySizeFirst<ResourceAssignedToFloorplan>(data.availableResources, size)
              .filter(resource => {
                let allPartyServicesPresentInResource = true;

                if (party?.services?.length > 0) {
                  for (const item of party.services) {
                    if (!allPartyServicesPresentInResource) {
                      return;
                    }

                    if (!resource.services.includes(item)) {
                      allPartyServicesPresentInResource = false;
                      break;
                    }
                  }
                }

                return /*size >= resource.capacity.min && */allPartyServicesPresentInResource;
              })
              .map((resource: ResourceAssignedToFloorplan) => {
                let isResourceOnFloorplan = false;
                if (this.floorplanResources) {
                  for (const item of this.floorplanResources) {
                    if (item.resources.includes(resource._id) && !isResourceOnFloorplan) {
                      isResourceOnFloorplan = true;

                      resource.onFloorplan = this.floorplanResources.length > 1
                        ? `${item.floorPlanName}`
                        : '';
                    }
                  }

                  if (!isResourceOnFloorplan) {
                    resource.onFloorplan = 'not in floorplan';
                  }
                }

                return resource;
              });
            // If a resource is missing from this list (e.g. it was disabled) clear it — but only
            // when something critical actually changed. An edit that never touches
            // services/resources/size/duration/time must leave the existing (possibly
            // since-disabled) assignment alone.
            if (this.resourcesInDropdown.length && (party.resources || []).length && !this.canBypassWorkTime()) {
              const resourceIds: string[] = party.resources.filter(resourceId => this.resourcesInDropdown.some(resource => resource._id === resourceId));
              if (resourceIds.sort().join() !== party.resources.sort().join()) {
                party.resources = party.resources.filter(resourceId => this.resourcesInDropdown.some(resource => resource._id === resourceId));
              }
            }

            if (this.resourcesEnabled) {
              this.showResourceAlert = true;
            }

            resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resourceIds);
          }

          if (data?.availableServices) {
            this.servicesInDropdown = data.availableServices;

            if (this.party.services?.length && this.party.duration <= 0) {
              this.party.duration = this.dateService.checkServicesDuration(this.servicesInDropdown, this.party.services, this.party.size);
              this.setPartyDurationInMinutes();
            }

            if (this.party.services && this.party.services.length > 0) {
              const shortenedDurationChanged = this.handleShortenedAlert();
              if (shortenedDurationChanged) {
                this.checkWorkTime();

                return;
              }
            }
          }

          if (!this.overlappingParty) {
            this.overlappingParty = [];
          }

          if (this.overlappingParty.length && this.onlineReservationEnabled) {
            if (this.allowMultipleParties || this.overrideNoResources) {
              this.showSizeAlert(resourcesCapacity);
            } else {
              this.showOverlappingAlert = true;
              const overlappingParty = this.overlappingParty[0];
              this.availablePartyDuration = overlappingParty.quoted_time - date;
              this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / this.msPerMin);
            }
          }

          // Protect an existing party's already-assigned service/resource from being silently swapped
          // or filled in by this autoselect. These check the party's true original assignment
          // (this.partyToEdit), not the enabled-only `services`/`resources` shown in the dropdown —
          // a disabled assignment is hidden from the picker, but it still counts as "already assigned"
          // for this guard, and only counts as absent when the party truly never had one.
          const blocksServiceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.services?.length ?? 0) > 0;
          const blocksResourceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.resources?.length ?? 0) > 0;
          if (this.servicesInDropdown?.length === 1 && !this.removeServices) {
            if (!blocksServiceAutoselect && (!services?.length || this.servicesInDropdown[0]._id !== services[0])) {
              // Autoselect service
              const serviceIdToSelect = this.servicesInDropdown[0]._id;
              this.party.services = [serviceIdToSelect];
              this.selectServices();

            } else if (services?.length === 1 && !resourceIds?.length && !blocksResourceAutoselect) {
              this.resourcesInDropdown = this.resourcesInDropdown.filter(resource => resource.services.includes(services[0]));
              if (
                this.resourcesEnabled &&
                this.user.resourceSettings?.resourceSuggestion === this.resourceSuggestionOption.auto &&
                this.resourcesInDropdown.length &&
                !this.removeResources &&
                !this.selectedResource
              ) {
                const resourceToSelect = this.resourcesInDropdown.find((availableResource) => {
                  if (availableResource.capacity.min > party.size || availableResource.capacity.max < party.size) {
                    return false;
                  }

                  return availableResource.services.includes(services[0]);
                });

                if (resourceToSelect !== undefined) {
                  this.party.resources = [resourceToSelect._id];
                  this.selectResources();
                }
              }
            }
          }

          this.isSubmitting = false;

          if (this.resourceOverrideCallback) {
            this.resourceOverrideCallback(this.party);
          }
        }),
        catchError((error) => {
          if (error.status === 401) {
            this.cancel();
          } else {
            this.alertsService.add({
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `resources and services not found: ${error}`,
            });
          }

          return of(error);
        }),
      )
      .subscribe();
  }

  calculateCapacityInSelectedResources(resources: Resource[] = [], selectedResourceIds: string[] = []): ICapacity | null {
    const fullDataOfSelectedResources = resources.filter(resource => selectedResourceIds.includes(resource._id));

    return this.calculateResourcesCapacity(fullDataOfSelectedResources);
  }

  calculateResourcesCapacity(resources: Resource[] = []): ICapacity | null {
    if (!resources || resources.length === 0) {
      return null;
    }

    return resources.reduce((acc, resource) => {
      acc.min += resource.capacity?.min || 0;
      acc.max += resource.capacity?.max || 0;
      return acc;
    }, {
      min: 0,
      max: 0
    });
  }

  selectServices() {
    if (this.party.services && this.party.services.length === 0) {
      // Reset errors if services removed from party
      this.removeServices = true;
      this.showResourceAlert = false;
      this.shortenedDurationState = null;
      this.durationRecalc.recompute();
      this.checkWorkTime();

      return;
    }
    this.removeServices = false;
    if (this.resourcesEnabled) {
      this.showResourceAlert = true;
    }
    this.party.duration = 0;
    this.durationRecalc.recompute();

    this.handleShortenedAlert();
    this.checkWorkTime();
  }

  doesEditingHaveAnyConflicts(): Record<string, boolean> {
    return {
      editFormInvalid: this.addEditForm.form.invalid,
      isOutOutBusinessHours: (!this.isWorkTime || this.isPartyInPastTime) && !this.overrideIsWorkTime,
      isAvailableTimeAndNoOverride: this.isAvailableTime && !this.overrideNoResources,
      noAvailService: this.noAvailableServices(this.addEditForm),
      noConfirm: this.isConfirmPartyButtonDisabled(),
      formPending: this.addEditForm.form.pending,
      submitting: this.isSubmitting,
    };
  }

  addResourceToSelectedOnes(resourceId: string, serviceIds: string[]): void {
    if (!this.party.resources) {
      this.party.resources = [resourceId];
      this.selectResources();
      return;
    }

    const doesExist = this.party?.resources?.findIndex((id) => id === resourceId);
    if (doesExist !== -1) {
      return;
    }

    this.party.resources = Array.from(new Set([...this.party.resources, resourceId]));
    this.party.services = [...serviceIds];
    this.selectResources();
  }

  selectResources(): void {
    this.durationRecalc.recompute();
    this.showResourceAlert = true;
    if (this.party.resources?.length) {
      this.removeResources = false;
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, this.party.resources);
      this.showSizeAlert(resourcesCapacity);
    } else {
      this.removeResources = true;
      this.showSizeAlert(null);
    }
    this.checkWorkTime();
  }

  overrideResources() {
    this.checkWorkTime();
  }

  clearServicesResources() {
    this.party.services = [];
    this.party.resources = [];
  }

  setAvailableDuration() {
    this.party.duration = this.availablePartyDuration;
    this.party.durationInMinutes = this.availablePartyDurationInMin;
    this.showOverlappingAlert = false;
    this.availablePartyDuration = 0;
  }

  setQuotedTime() {
    this.currentTime = moment.tz(this.timezone);
    this.currentTime.add(this.party.quoted_time_minutes, 'minutes');
    this.clearServicesResources();
    this.checkWorkTime();
  }

  changeQuoted(num, model = 'quoted_time_minutes', modelToAddMilliseconds = '') {
    this.addEditForm.form.markAsDirty();
    this.party[model] += num;
    if (modelToAddMilliseconds) {
      this.party[modelToAddMilliseconds] += num * this.msPerMin;
    } else {
      this.clearServicesResources();
    }
    if (model === 'quoted_time_minutes') {
      this.currentTime.add(num, 'minutes');
    }
    this.checkWorkTime();
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);

    delete party.date;
    delete party.time;
    delete party.durationInMinutes;
    delete party.quoted_time_minutes;
    delete party.className;
    delete party.assignedResources;

    if (this.party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      party.quoted_time = Number(this.currentTime.format('x'));
      this.isWorkTime = false;

      return;
    }

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - this.msPerMin &&
      party.quoted_time < this.originalQuotedTime + this.msPerMin
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - this.now) / this.msPerMin));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity | null): void {
    const message = this.resourcesInDropdown?.length
      ? this.configureParty.buildResourceSizeAlert({
          actualSize: this.party?.size,
          capacity: resourcesCapacity,
          customerLabel: this.displayOptionCustomerLabel,
          resourceLabel: this.resourceSettingsLabel,
          allowMultipleParties: this.allowMultipleParties,
          partySizeForTimeSlots: this.partySizeForTimeSlots,
          overlappingParties: this.overlappingParty,
        })
      : null;
    this.resourceSizeAlert = message;
  }

  sizeChanged(): boolean {
    this.showSizeValidationMessage = false;
    this.resourceSizeAlert = null;
    const { resources, size } = this.party;
    if (this.resourcesEnabled && this.party?.resources?.length) {
      const resourcesCapacity = this.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      this.showSizeAlert(resourcesCapacity);
      this.sizeOther = size < 8 ? '' : size;

      return false;
    }

    this.checkWorkTime();
  }

  changeSize(): void {
    this.showSizeValidationMessage = false;
    this.party.size = this.sizeOther || this.user.default_size;
    this.sizeChanged();
  }

  partyPhoneLookup(): void {
    const { phone } = this.party;
    if (!phone ||
      phone === '' ||
      typeof phone === 'undefined' ||
      phone.length < this.shortestPhoneDigits
    ) {
      return;
    }
    const parsedPhone = this.telService.format(phone, this.user.country, 'intl');
    if (typeof parsedPhone !== 'undefined' && parsedPhone.trim().length >= this.shortestPhoneDigits) {
      this.customerService.getByPhone(parsedPhone)
        .pipe(
          tap(data => {
            if (data) {
              const customerCustomFields: MergedCustomFields = extractPartyFieldsFromCustomer(this.user.partyFields, data);
              const _data = {
                name: this.party.name || customerCustomFields.name,
                customFields: mergeCustomFields(this.party.customFields, customerCustomFields.customFields),
              };
              this.party = {
                ...this.party,
                ..._data,
              };
              this.customer = data;
            } else {
              this.customer = null;
            }
          }),
        )
        .subscribe();
    }
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity && item?.capacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  changeDuration() {
    this.party.duration = this.party.durationInMinutes * this.msPerMin;
    this.checkWorkTime();
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || !!this.resourceSizeAlert);
  }

  changeDate() {
    if (
      this.selectedTimeMode ||
      !this.party.time ||
      !this.party.time.includes('M') ||
      typeof this.party.date === 'string' ||
      this.party.date === null
    ) {
      return false;
    }

    this.currentTime = this.dateTimeService.setTimeToDate(
      this.dateTimeService.ngbDateToMoment(this.party.date, this.timezone),
      this.party.time,
      this.timezone,
    );

    if (this.servicesEnabled && this.onlineReservationEnabled && !this.resourcesEnabled) {
      this.clearServicesResources();
    }
    this.overrideIsWorkTime = false;
    this.checkWorkTime();
  }

  clean(obj) {
    for (const propName in obj) {
      if (propName === 'messagingProvider') {
        continue;
      }

      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
  }

  saveParty(showSuccessUpdateAlert = true): void {
    const party = { ...this.party };
    this.clean(party);
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    this.convertQuotedTime(party);
    if (this.isWorkTimeBlockingSave()) {
      this.isSubmitting = false;

      return;
    }

    if (party.resource && !(party.resources || []).length) {
      if (!Array.isArray(party.resource)) {
        party.resources = [party.resource];
      } else {
        party.resources = party.resource;
      }
      delete party.resource;
    }

    // The services/resources fields only ever show/offer enabled ids, so a disabled assignment is
    // never visible to touch. If the user left a field exactly as it was when the form opened,
    // restore the party's true original ids (which may include a since-disabled one) rather than
    // saving the enabled-only value the picker displayed — an edit to an unrelated field (e.g.
    // Notes) must not silently drop a disabled service/resource assignment.
    if (this.isEditMode) {
      if (partyIdsEqual(party.services, this.originalCritical?.services)) {
        party.services = this.partyToEdit.services || [];
      }
      if (partyIdsEqual(party.resources, this.originalCritical?.resources)) {
        party.resources = this.partyToEdit.resources || [];
      }
      // hasDisabledServicesOnInit means initEditMode recalculated duration from only the
      // enabled subset of services (possibly 0), overwriting the party's true duration in the
      // form. If the user never touched the duration field since, save the true original value
      // instead of the recalculated one, or a disabled-then-reenabled service permanently loses
      // its duration.
      if (this.hasDisabledServicesOnInit && party.duration === this.originalCritical?.duration) {
        party.duration = this.partyToEdit.duration;
      }
    }

    party.overrideNoResources = this.overrideNoResources;
    party.overrideBlockedTime = this.overrideBlockedTime;

    if (this.shouldForceOverrideNoResources()) {
      party.overrideNoResources = true;
    }

    if (this.shouldForceOverrideBlockedTime()) {
      party.overrideBlockedTime = true;
    }

    if (party.type !== PartyTypes.reservation && !Boolean(this.partyToEdit)) {
      party.checkin_time = party.checkin_time || Date.now();
    }

    this.savePartyAction(party, 'updated successfully', showSuccessUpdateAlert);
  }

  private isWorkTimeBlockingSave(): boolean {
    // Mirror the on-screen schedule warning (checkIfOutOfBusinessHours() is its condition), not
    // isEditMode: a guard stricter than the Confirm button turns a click into a silent no-op, and
    // one keyed on isEditMode drops the check for every edit rather than only the bypassed ones.
    return !!this.addEditForm && this.checkIfOutOfBusinessHours(this.addEditForm);
  }

  /**
   * The blocked-time warning and its "Override?" checkbox are not shown to a served party, so the
   * user never gets to tick it — the save has to carry the override itself. Without this the
   * backend refuses the update ("Time is blocked: …") on a party that is already at the table.
   */
  private shouldForceOverrideBlockedTime(): boolean {
    return isPartySeated(this.partyToEdit);
  }

  private shouldForceOverrideNoResources(): boolean {
    return (!this.showOverlappingAlert && this.isEditMode) || this.canBypassWorkTime();
  }

  cancel(): void {
    void this.modalController.dismiss(null);
  }

  ok(showSuccessUpdateAlert = true): void {
    const { phone } = this.party;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }

            this.actionAfterCloseModal(showSuccessUpdateAlert);
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal(showSuccessUpdateAlert);
  }

  actionAfterCloseModal(showSuccessUpdateAlert = true): void {
    const { size, sizeOther } = this.party;
    if (this.user.waitFields.size &&
      size &&
      typeof size !== 'number' ||
      sizeOther &&
      typeof sizeOther !== 'number') {
      this.showSizeValidationMessage = true;

      return;
    }
    this.isSubmitting = true;
    this.saveParty(showSuccessUpdateAlert);
  }

  isSubmitDisabled(addEditForm: NgForm): boolean {
    if (!addEditForm) {
      return;
    }

    const isAvailableTimeAndNoOverride = Boolean(this.isAvailableTime) && !this.overrideNoResources;
    const isBlockedTimeAndNoOverride = this.showBlockedTimeWarning();
    const isOutOutBusinessHours = this.requiresBusinessHoursOverride();
    const needsShortenedDurationConfirmation =
      Boolean(this.shortenedDurationState?.requiresConfirmation) && !this.shortenedDurationConfirmed;

    if (this.isEditMode) {
      // Every warning this form can raise is mirrored here, so none of them can sit on screen next
      // to an enabled Confirm: checkIfOutOfBusinessHours() is exactly the template's schedule
      // warning, noAvailableServices() its availability warning, isConfirmPartyButtonDisabled()
      // covers overlap + resource capacity, plus the "no available table" alert, the blocked-time
      // warning and the shortened-duration prompt. Only the add-mode business-hours check (which
      // ignores the seated bypass) is left out — an existing party must not re-run it.
      return addEditForm.form.invalid || addEditForm.form.pending || this.isSubmitting ||
        this.checkIfOutOfBusinessHours(addEditForm) ||
        this.noAvailableServices(addEditForm) ||
        isAvailableTimeAndNoOverride ||
        isBlockedTimeAndNoOverride ||
        needsShortenedDurationConfirmation ||
        this.isConfirmPartyButtonDisabled();
    }

    return addEditForm.form.invalid ||
      isOutOutBusinessHours ||
      this.noAvailableServices(addEditForm) ||
      this.isConfirmPartyButtonDisabled() ||
      addEditForm.form.pending ||
      this.isSubmitting ||
      isAvailableTimeAndNoOverride ||
      isBlockedTimeAndNoOverride ||
      needsShortenedDurationConfirmation;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  checkIfOutOfBusinessHours(addEditForm: NgForm): boolean {
    return this.isOutOfBusinessHours(addEditForm) && !this.overrideIsWorkTime;
  }

  setPartyDurationInMinutes() {
    this.party.durationInMinutes = Math.round(this.party.duration / this.msPerMin) || 0;
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  isUsOrCaPhoneNumber(): boolean {
    return isUsOrCaPhoneNumber(this.party.phone);
  }

  changeMessagingProvider() {
    if (!this.party.phone || isUsOrCaPhoneNumber(this.party.phone)) {
      this.party.messagingProvider = null;
    }
  }

  private savePartyAction(party: IParty, successMessage = 'updated successfully', showSuccessUpdateAlert = true): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          // Eagerly sync store so UI reflects the new/updated party even if
          // the websocket event is lost or delayed. WS handlers dedupe via upsert.
          this.activePartiesService.upsertParty(data);
          void this.modalController.dismiss(data);
          if (showSuccessUpdateAlert) {
            this.alertsService.add({
              type: IAlertTypes.success,
              heading: 'Success:',
              body: successMessage,
            });
          }
        }),
        catchError((err) => {
          if (err?.reloadData) {
            this.party.resources = [];
            this.checkWorkTime();
          }

          const customer = this.user.displayOptions?.labels?.customer || 'customer';
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${customer} not added successfully: ${err?.message}`,
          });

          return of(err);
        }),
        finalize(() => {
          this.isSavingParty = false;
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private handleShortenedAlert(): boolean {
    let shortenedDurationChanged = false;
    // Calculate shortened duration state and apply adjusted duration if needed
    const prevRequiresConfirmation = this.shortenedDurationState?.requiresConfirmation ?? false;
    // A served party keeps the duration it was seated with, so the shortened-duration warning is
    // not raised on its edit form. A null state also clears the Confirm gate below, which has to
    // go with the warning — otherwise the button is disabled with nothing explaining it.
    const state = isPartySeated(this.partyToEdit)
      ? null
      : calculateShortenedDurationState(this.servicesInDropdown, this.party?.services || []);
    this.shortenedDurationState = state;

    // In edit mode never write the adjusted duration back (it would silently change the party's duration).
    if (
      state !== null &&
      !this.canBypassWorkTime() &&
      state.adjustedDurationMs !== null &&
      (!this.party.duration || this.party.duration > state.adjustedDurationMs)
    ) {
      this.party.duration = state.adjustedDurationMs;
      this.party.durationInMinutes = Math.round(state.adjustedDurationMs / this.msPerMin);
      shortenedDurationChanged = true;
      this.isWorkTime = this.providerScheduleService.comparisonTime(
        this.currentTime.valueOf(),
        this.workHours,
        state.adjustedDurationMs,
      );
    }

    if (!state?.requiresConfirmation) {
      this.shortenedDurationConfirmed = true;
    } else if (!prevRequiresConfirmation) {
      this.shortenedDurationConfirmed = false;
    }

    return shortenedDurationChanged;
  }

  private get hasGuestPerSlotLimit(): boolean {
    return this.bookingsPerTimeSlotEnabled && this.bookingsPerTimeSlotByGuests;
  }

  private isSlotFullByGuests(): boolean {
    if (!this.bookingsPerTimeSlotEnabled || !this.bookingsPerTimeSlotByGuests || !this.isReservation) {
      return false;
    }

    const slotTime = this.currentTime.valueOf();
    const guestsInSlot = (this.parties || [])
      .filter(party => party.type === PartyTypes.reservation &&
        party._id !== this.party._id &&
        Number(party.quoted_time) === slotTime)
      .reduce((acc, party) => acc + (Number(party.size) || 0), 0);

    return guestsInSlot + Number(this.party.size || 0) > this.bookingsPerTimeSlotQuantity;
  }

  getServiceOptionName = (service: ServiceWithAvailability): string => {
    return getServiceOptionNameWithWarning(service);
  };
}
